import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import { useCSRFHeaders } from '../../../components/auth/CSRFProvider'
import { 
  Upload, 
  Save, 
  Image as ImageIcon,
  Loader,
  Trash2
} from 'lucide-react'
import { formatPrice } from '../../../lib/utils/format'
import type { Category } from '@pranala/shared'

export const Route = createFileRoute('/admin/products/create')({
  beforeLoad: async () => {
    // Admin check will be handled by the layout or API
    // The API will return 401/403 if not authorized
  },
  loader: async () => {
    // Fetch categories from API
    try {
      const response = await fetch('/api/categories')
      if (!response.ok) {
        throw new Error('Failed to fetch categories')
      }
      const data = await response.json()
      return data.categories as Category[]
    } catch (error) {
      console.error('Failed to load categories:', error)
      return [] as Category[]
    }
  },
  component: CreateProduct,
})

function CreateProduct() {
  const navigate = useNavigate()
  const csrfHeaders = useCSRFHeaders()
  const categories = Route.useLoaderData() ?? []
  
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    description: '',
    price: '',
    originalPrice: '',
    category: '',
    isbn: '',
    publishedYear: new Date().getFullYear().toString(),
    pages: '',
    language: 'Indonesia',
    publisher: 'Pustaka Pranala',
    weight: '',
    dimensions: '',
    stock: '',
    status: 'published',
    featured: false
  })

  const [images, setImages] = useState<File[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string>('')

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    setImages(prev => [...prev, ...files])
    
    // Create preview for first image
    if (files.length > 0 && files[0]) {
      const reader = new FileReader()
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string)
      }
      reader.readAsDataURL(files[0])
    }
  }

  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index))
    if (index === 0) {
      setPreviewUrl('')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      // Validate required fields
      if (!formData.title || !formData.description || !formData.price || !formData.category) {
        alert('Judul, deskripsi, harga, dan kategori wajib diisi')
        setIsLoading(false)
        return
      }

      // Call API to create product
      const response = await fetch('/api/products', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...csrfHeaders,
        },
        body: JSON.stringify({
          title: formData.title,
          description: formData.description,
          price: parseFloat(formData.price),
          salePrice: formData.originalPrice ? parseFloat(formData.originalPrice) : undefined,
          category: formData.category,
          author: formData.author || undefined,
          isbn: formData.isbn || undefined,
          publishedYear: formData.publishedYear ? parseInt(formData.publishedYear) : undefined,
          pages: formData.pages ? parseInt(formData.pages) : undefined,
          language: formData.language,
          publisher: formData.publisher || undefined,
          weight: formData.weight ? parseInt(formData.weight) : undefined,
          dimensions: formData.dimensions || undefined,
          stockQuantity: formData.stock ? parseInt(formData.stock) : 0,
          status: formData.status,
          featured: formData.featured,
        }),
      })

      const result = await response.json()

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Gagal menambahkan buku')
      }

      alert('Buku berhasil ditambahkan!')
      navigate({ to: '/admin/products' })
    } catch (error) {
      console.error('Error creating product:', error)
      alert(error instanceof Error ? error.message : 'Gagal menambahkan buku. Silakan coba lagi.')
    } finally {
      setIsLoading(false)
    }
  }

  const formatPriceDisplay = (price: string) => {
    const num = parseFloat(price) || 0
    return formatPrice(num)
  }

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4 border-b border-neutral-200">
        <div>
          <h1 className="text-2xl lg:text-3xl text-neutral-900 tracking-tight">
            Tambah Buku Baru
          </h1>
          <p className="text-neutral-500 mt-1">
            Tambahkan buku baru ke katalog toko Anda
          </p>
        </div>
        
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            asChild
            className="border-neutral-200 hover:border-yellow-400 hover:text-yellow-600 rounded-lg"
          >
            <Link to="/admin/products">Batal</Link>
          </Button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <Card className="border border-neutral-100 shadow-sm">
              <CardHeader className="border-b border-neutral-100">
                <CardTitle className="text-lg font-semibold text-neutral-900">Informasi Dasar</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Judul Buku *
                    </label>
                    <Input
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="Masukkan judul buku"
                      required
                      className="rounded-lg border-neutral-200 focus:border-yellow-400 focus:ring-yellow-400"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Penulis
                    </label>
                    <Input
                      name="author"
                      value={formData.author}
                      onChange={handleInputChange}
                      placeholder="Masukkan nama penulis"
                      className="rounded-lg border-neutral-200 focus:border-yellow-400 focus:ring-yellow-400"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Deskripsi *
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="Masukkan deskripsi buku"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Kategori *
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                      required
                    >
                      <option value="">Pilih kategori</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.name}>{category.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Bahasa
                    </label>
                    <select
                      name="language"
                      value={formData.language}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    >
                      <option value="Indonesia">Indonesia</option>
                      <option value="English">English</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Status
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pricing */}
            <Card className="border border-neutral-100 shadow-sm">
              <CardHeader className="border-b border-neutral-100">
                <CardTitle className="text-lg font-semibold text-neutral-900">Harga</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Harga Jual *
                    </label>
                    <Input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      placeholder="0"
                      min="0"
                      step="1000"
                      required
                      className="rounded-lg border-neutral-200 focus:border-yellow-400 focus:ring-yellow-400"
                    />
                    {formData.price && (
                      <p className="text-sm text-yellow-600 font-medium mt-1">
                        {formatPriceDisplay(formData.price)}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Harga Asli
                    </label>
                    <Input
                      type="number"
                      name="originalPrice"
                      value={formData.originalPrice}
                      onChange={handleInputChange}
                      placeholder="0"
                      min="0"
                      step="1000"
                      className="rounded-lg border-neutral-200 focus:border-yellow-400 focus:ring-yellow-400"
                    />
                    {formData.originalPrice && (
                      <p className="text-sm text-neutral-600 font-medium mt-1">
                        {formatPriceDisplay(formData.originalPrice)}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Additional Details */}
            <Card className="border border-neutral-100 shadow-sm">
              <CardHeader className="border-b border-neutral-100">
                <CardTitle className="text-lg font-semibold text-neutral-900">Detail Tambahan</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      ISBN
                    </label>
                    <Input
                      name="isbn"
                      value={formData.isbn}
                      onChange={handleInputChange}
                      placeholder="978-xxx-xxx-xxx-x"
                      className="rounded-lg border-neutral-200 focus:border-yellow-400 focus:ring-yellow-400"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Tahun Terbit
                    </label>
                    <Input
                      type="number"
                      name="publishedYear"
                      value={formData.publishedYear}
                      onChange={handleInputChange}
                      min="1900"
                      max={new Date().getFullYear() + 1}
                      className="rounded-lg border-neutral-200 focus:border-yellow-400 focus:ring-yellow-400"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Jumlah Halaman
                    </label>
                    <Input
                      type="number"
                      name="pages"
                      value={formData.pages}
                      onChange={handleInputChange}
                      placeholder="0"
                      min="1"
                      className="rounded-lg border-neutral-200 focus:border-yellow-400 focus:ring-yellow-400"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Berat (gram)
                    </label>
                    <Input
                      type="number"
                      name="weight"
                      value={formData.weight}
                      onChange={handleInputChange}
                      placeholder="0"
                      min="1"
                      className="rounded-lg border-neutral-200 focus:border-yellow-400 focus:ring-yellow-400"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Dimensi
                    </label>
                    <Input
                      name="dimensions"
                      value={formData.dimensions}
                      onChange={handleInputChange}
                      placeholder="15 x 23 cm"
                      className="rounded-lg border-neutral-200 focus:border-yellow-400 focus:ring-yellow-400"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Penerbit
                    </label>
                    <Input
                      name="publisher"
                      value={formData.publisher}
                      onChange={handleInputChange}
                      placeholder="Nama penerbit"
                      className="rounded-lg border-neutral-200 focus:border-yellow-400 focus:ring-yellow-400"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Stok
                    </label>
                    <Input
                      type="number"
                      name="stock"
                      value={formData.stock}
                      onChange={handleInputChange}
                      placeholder="0"
                      min="0"
                      className="rounded-lg border-neutral-200 focus:border-yellow-400 focus:ring-yellow-400"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="featured"
                    checked={formData.featured}
                    onChange={(e) => setFormData(prev => ({ ...prev, featured: e.target.checked }))}
                    className="rounded border-neutral-300 text-yellow-500 focus:ring-yellow-400"
                  />
                  <label className="text-sm font-medium text-neutral-700">
                    Produk Unggulan
                  </label>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Image Upload */}
          <div className="space-y-6">
            <Card className="border border-neutral-100 shadow-sm">
              <CardHeader className="border-b border-neutral-100">
                <CardTitle className="text-lg font-semibold text-neutral-900">Gambar Produk</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-6">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Unggah Gambar
                  </label>
                  <div className="border-2 border-dashed border-neutral-200 rounded-xl p-6 text-center hover:border-yellow-400 transition-colors">
                    <Upload className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
                    <p className="text-sm text-neutral-600 mb-4">
                      Klik untuk unggah atau seret dan lepas
                    </p>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="file-upload"
                    />
                    <label
                      htmlFor="file-upload"
                      className="cursor-pointer bg-yellow-500 hover:bg-yellow-400 text-white px-4 py-2 rounded-lg transition-colors font-medium"
                    >
                      Pilih File
                    </label>
                  </div>
                </div>

                {/* Image Preview */}
                {previewUrl && (
                  <div className="space-y-4">
                    <div className="text-sm font-medium text-neutral-700">Preview</div>
                    <div className="relative">
                      <img
                        src={previewUrl}
                        alt="Product preview"
                        className="w-full h-64 object-cover rounded-xl"
                        loading="lazy"
                        decoding="async"
                        width={600}
                        height={400}
                      />
                    </div>
                  </div>
                )}

                {/* Uploaded Files */}
                {images.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-neutral-700">
                      File Terunggah ({images.length})
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {images.map((file, index) => (
                        <div key={index} className="relative group border border-neutral-200 rounded-xl p-2">
                          <div className="aspect-square bg-neutral-100 rounded-lg flex items-center justify-center">
                            <ImageIcon className="w-8 h-8 text-neutral-400" />
                          </div>
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                          <div className="text-xs text-neutral-600 mt-1 truncate">
                            {file.name}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-4 pt-4 border-t border-neutral-200">
          <Button 
            variant="outline" 
            type="button" 
            asChild
            className="border-neutral-200 hover:border-yellow-400 hover:text-yellow-600 rounded-lg"
          >
            <a href="/admin/products">Batal</a>
          </Button>
          <Button 
            type="submit" 
            disabled={isLoading}
            className="bg-yellow-500 hover:bg-yellow-400 text-white font-medium rounded-lg border-0 shadow-sm hover:shadow-md transition-all"
          >
            {isLoading ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Menyimpan...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Simpan Buku
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  )
}
